import { userManager } from '@libs/oidc';
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';

import { isNewOms } from '@core/order';

function formatAuthorizationHeader(token: string) {
  return `Bearer ${token}`;
}

export function resolveToken(
  getToken: () => Promise<string | null | undefined>
) {
  return (config: InternalAxiosRequestConfig) => {
    if (config.ignoreAuth) {
      return config;
    }

    return getToken().then((token) => {
      if (config.headers && token) {
        config.headers.Authorization = formatAuthorizationHeader(token);

        //TODO: Remove after all users migrate to New OMS
        config.headers.legacy = String(!isNewOms.value);
      }

      return config;
    });
  };
}

let isRefreshing = false;
const refreshQueue: Array<() => void> = [];

function subscribeTokenRefresh(cb: () => void) {
  refreshQueue.push(cb);
}

function afterRefreshCallback() {
  refreshQueue.map((cb) => cb());
}

// credit: https://gist.github.com/mkjiau/650013a99c341c9f23ca00ccb213db1c
export function renewToken(axiosInstance: AxiosInstance) {
  return (error: AxiosError) => {
    if (error?.response?.status !== 401) {
      return Promise.reject(error);
    }

    if (!isRefreshing) {
      isRefreshing = true;

      userManager
        .signinSilent()
        .then(() => {
          isRefreshing = false;
          afterRefreshCallback();
        })
        .catch(async () => {
          await userManager.removeUser();
          await userManager.clearStaleState();
          await userManager.signinRedirect({
            state: { fromUrl: window.location.pathname },
          });
        });
    }

    return new Promise((resolve) => {
      subscribeTokenRefresh(() => {
        resolve(axiosInstance(error.config as AxiosRequestConfig));
      });
    });
  };
}

export async function revokeSession(error: AxiosError) {
  if (
    error?.response?.status === 401 &&
    'x-session-revoked' in error.response.headers
  ) {
    await userManager.clearStaleState();
    await userManager.signoutRedirect();
    return;
  }
  return Promise.reject(error);
}
